import { useMutation } from '@apollo/client';
import type {
  AddRiskToEnterpriseRiskInput,
  AddRiskToEnterpriseRiskMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  AddRiskToEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useAddRiskToEnterpriseRiskTRPC } from './useAddRiskToEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (COI-432). Delegates to the tRPC twin when the `trpc` flag
 * is on, else the legacy Apollo Hasura-action path. Consumers import THIS, never
 * the *TRPC twin (see packages/web/CLAUDE.md → "Mutation Hook Wrapper Pattern").
 */
export const useAddRiskToEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const trpcMutation = useAddRiskToEnterpriseRiskTRPC();

  const [addRiskToEnterpriseRiskGraphQL, { loading, error }] = useMutation(
    AddRiskToEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk');
        evictField(cache, 'risk_score');
        evictField(cache, 'enterprise_risk');
        evictField(cache, 'enterprise_risk_score');
      },
      refetchQueries: [namedOperations.Query.getRiskById],
    }
  );

  const addRiskToEnterpriseRisk = async (
    objects: AddRiskToEnterpriseRiskInput[]
  ): Promise<AddRiskToEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.addRiskToEnterpriseRisk(objects);
    }

    const result = await addRiskToEnterpriseRiskGraphQL({
      variables: { objects },
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.frontend.risk.riskById.queryKey(),
    });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      addRiskToEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return { addRiskToEnterpriseRisk, loading, error };
};
